import Guid from "@/app/types/common/guid";
import environment from "@/environment";

/**
 * Basic tracing interface.
 * We don't push the actual traces to the server, so they're only understood implicitly when child spans are observed.
 */

type SpanLabels = {
  [key: string]: string;
};

interface Span {
  displayName?: string;
  parentSpanId?: number;
  startTime: number;
  endTime?: number;
  labels: SpanLabels;
  spanId: number;
  traceId: string;
}

class Trace {
  private readonly _traceId: string | null;
  private _spanStack: Span[];
  private _allSpans: Span[];
  constructor(traceEnabled: boolean) {
    // Random 32-character hex string
    this._traceId = traceEnabled
      ? Guid.create().toString().replaceAll("-", "")
      : null;
    this._spanStack = [];
    this._allSpans = [];
  }

  public getAllSpans() {
    return [...this._allSpans];
  }

  public printTraceToConsole() {
    const minTime = Math.min(...this._allSpans.map((span) => span.startTime));
    const maxTime = Math.max(
      ...this._allSpans.map((span) => span.endTime || 0)
    );

    /* eslint-disable no-console */
    console.log(
      "++ Trace %s (%f seconds)",
      this.traceId,
      (maxTime - minTime) / 1000
    );
    for (const span of this._allSpans) {
      const logText =
        span.displayName +
        " (" +
        span.spanId +
        ") " +
        ((span.endTime || span.startTime) - span.startTime) / 1000 +
        "s";
      /* eslint-disable no-console */
      console.log(logText);
    }
  }

  public get traceId() {
    return this._traceId;
  }

  public get lastTime() {
    return Math.max(
      ...this._allSpans.map((span: Span) => span.endTime || span.startTime)
    );
  }

  public get spanId() {
    return this._spanStack.length > 0
      ? this._spanStack[this._spanStack.length - 1].spanId
      : undefined;
  }

  public pushSpan(displayName?: string, labels?: SpanLabels) {
    const newSpan = this.createDetachedSpan(displayName, labels);
    this._spanStack.push(newSpan);
    this._allSpans.push(newSpan);
  }

  public popSpan(matchingLabels?: SpanLabels) {
    let span: Span | undefined = undefined;

    if (matchingLabels) {
      for (let idx = this._spanStack.length - 1; idx >= 0; idx = idx - 1) {
        const innerSpan = this._spanStack[idx] as Span;
        if (
          Object.entries(matchingLabels).every(([key, value]) => {
            return (
              innerSpan.labels != undefined && innerSpan.labels[key] !== value
            );
          })
        ) {
          // This is the span you are looking for
          this._spanStack.splice(idx, 1);
          span = innerSpan;
          break;
        }
      }
    } else {
      span = this._spanStack.pop();
    }
    if (span != undefined) {
      span.endTime = Date.now();
    }
  }

  public createDetachedSpan(displayName?: string, labels?: SpanLabels) {
    return {
      traceId: this._traceId as string,
      parent_span_id: this.spanId,
      displayName,
      spanId: Math.floor(Math.random() * Number.MAX_SAFE_INTEGER),
      startTime: Date.now(),
      labels: Object.assign({}, labels),
    };
  }

  public addDetachedSpan(span: Span) {
    span.endTime = Date.now();
    this._allSpans.push(span);
  }

  public isActive(): boolean {
    return this._spanStack.length > 0;
  }
}

class Tracer {
  private _currentTrace: Trace | null = null;

  public printTracesToConsole = true;

  public getCurrentTrace() {
    if (this._currentTrace == null) {
      return null;
    } else if (this._currentTrace.traceId == null) {
      return null;
    } else {
      return this._currentTrace;
    }
  }

  // Push and Pop are the easiest API for standard code:
  // but event based code may need to use the startPossibleSpan / recordSpanComplete API
  public pushSpan(
    forceTrace?: boolean,
    displayName?: string,
    labels?: SpanLabels
  ) {
    const trace = this.getOrCreateTrace(forceTrace || false);
    trace.pushSpan(displayName, labels);
  }

  public popSpan(matchingLabels?: SpanLabels) {
    this._currentTrace?.popSpan(matchingLabels);

    if (
      this._currentTrace != null &&
      !this._currentTrace.isActive() &&
      this.printTracesToConsole
    ) {
      // TODO: Ship spans off to the server for analysis
      // There's a full log in _currentTrace.getAllSpans();
      this._currentTrace.printTraceToConsole();
    }
  }

  // startPossibleSpan / recordSpanComplete are API for event-based code,
  // where an event saying "started" may or may not be followed with an event saying "complete"
  public startPossibleSpan(displayName?: string, labels?: SpanLabels) {
    return this.getOrCreateTrace(false).createDetachedSpan(displayName, labels);
  }

  public recordSpanComplete(span: Span) {
    if (this._currentTrace?.traceId === span.traceId) {
      this._currentTrace.addDetachedSpan(span);
      if (this.printTracesToConsole) {
        this._currentTrace.printTraceToConsole();
      }
    }
    // TODO: Could ship this to the server, but if we have started a different trace then trace data is confused..
  }

  public wrapPromiseInSpan<T>(
    promise: Promise<T>,
    forceTrace?: boolean,
    displayName?: string,
    labels?: SpanLabels
  ): Promise<T> {
    this.pushSpan(forceTrace, displayName, labels);
    return promise.finally(() => {
      this.popSpan(labels);
    });
  }

  /* If we're in the middle of a trace then return that - otherwise return a new trace */
  private getOrCreateTrace(forceTrace: boolean): Trace {
    if (
      this._currentTrace &&
      this._currentTrace.isActive() &&
      !this._currentTrace.traceId &&
      forceTrace
    ) {
      // Need to trace, but we're not currently actively tracing - so start now
      this._currentTrace = new Trace(true);
    }

    if (this._currentTrace != null && !this._currentTrace.isActive()) {
      // If we have a trace which completed very recently then keep using it.
      // Otherwise, chuck this
      const timeSinceLast = Date.now() - this._currentTrace.lastTime;
      const ONE_FRAME = 1000 / 30;
      if (timeSinceLast > 2 * ONE_FRAME) {
        this._currentTrace = null;
      }
    }

    if (this._currentTrace == null) {
      // No current trace: Decide if we should actively trace this..
      this._currentTrace = new Trace(this.shouldTrace());
    }
    return this._currentTrace;
  }

  private shouldTrace(): boolean {
    // return (Math.random() > 0.1);
    return !environment.isDevelopment;
  }
}

export const tracer = new Tracer();
